/**
 * Partners Page
 * List and manage partner organizations and their affiliates
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPartners } from "../services/partners.service";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import type { PartnerWithStats, PartnerFilters } from "../types/partner.types";

const Partners: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const limit = 25;

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Build filters
  const filters: PartnerFilters = {
    search: searchQuery || undefined,
    is_active: statusFilter === "all" ? undefined : statusFilter === "active",
  };

  // Fetch partners
  const {
    data: partnersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["partners", page, limit, filters],
    queryFn: () => getPartners({ page, limit }, filters),
  });

  const partners = partnersData?.data || [];
  const totalPages = partnersData?.totalPages || 1;

  // Summary stats
  const stats = {
    total: partnersData?.total || 0,
    active: partners.filter((p) => p.is_active).length,
    totalAffiliates: partners.reduce(
      (sum, p) => sum + (p.total_affiliates || 0),
      0,
    ),
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading partners..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card
        variant="outlined"
        padding="md"
        className="border-red-200 bg-red-50"
      >
        <p className="text-red-800">
          Error loading partners. Please try again.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Partners</h1>
          <p className="mt-2 text-gray-600">
            Manage partner organizations and their affiliate businesses
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/partners/new")}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Partner
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.total}
              </div>
              <div className="text-sm text-gray-500">Total Partners</div>
            </div>
          </div>
        </Card>
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.active}
              </div>
              <div className="text-sm text-gray-500">Active Partners</div>
            </div>
          </div>
        </Card>
        <Card variant="outlined" padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BuildingStorefrontIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.totalAffiliates.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Affiliates</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="outlined" padding="sm">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search partners by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </Card>

      {/* Partners Table */}
      <Card variant="outlined" padding="none">
        {partners.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Affiliates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacts
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {partners.map((partner: PartnerWithStats) => (
                  <tr
                    key={partner.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/partners/${partner.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {partner.name}
                        </div>
                        {partner.title && (
                          <div className="text-sm text-gray-500">
                            {partner.title}
                          </div>
                        )}
                        {partner.email && (
                          <div className="text-xs text-gray-400">
                            {partner.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        variant={partner.is_active ? "success" : "default"}
                      >
                        {partner.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {partner.city && partner.state ? (
                        <span>
                          {partner.city}, {partner.state}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <BuildingStorefrontIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {partner.active_affiliates || 0}
                        </span>
                        <span className="text-gray-400 text-sm">
                          / {partner.total_affiliates || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {partner.total_contacts || 0}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/partners/${partner.id}`);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchQuery || statusFilter !== "all"
                ? "No partners found matching your filters."
                : "No partners yet."}
            </p>
            <p className="text-gray-400 mt-2">
              {searchQuery || statusFilter !== "all"
                ? "Try different filters."
                : "Add your first partner to get started."}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <Button
                variant="primary"
                onClick={() => navigate("/partners/new")}
                className="mt-4"
              >
                <PlusIcon className="h-5 w-5 mr-2 inline" />
                Add Partner
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, partnersData?.total || 0)} of{" "}
            {partnersData?.total || 0} partners
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Help Section */}
      <Card
        variant="outlined"
        padding="md"
        className="bg-blue-50 border-blue-200"
      >
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">
              About Partners & Affiliates
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Partners are organizations that manage multiple affiliate
              businesses. Each affiliate has a unique 6-character code used for
              tracking and API access. When a partner is deactivated, all its
              affiliates are automatically deactivated as well.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Partners;
